import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  message,
  Upload,
  Typography,
  Divider,
  DatePicker,
  Select,
  Spin,
  Descriptions,
  Tabs
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  UploadOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  CreditCardOutlined,
  LockOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
// import { useAuth } from '@/contexts/AuthContext';
import UserService from '@/services/user.service';
import apiService from '@/services/api.service';
import { logger } from '@/utils/logger';
import type { UploadProps } from 'antd/es/upload/interface';
import type { RcFile } from 'antd/es/upload';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { getDateValueFromEvent, datePickerProps } from '@/utils/datePickerUtils';
import ParticipantStatistics from './ParticipantStatistics';
import { StyledAvatar, ProfileCard } from './styles/ProfilePage.styles';

dayjs.locale('ru');

const { Title } = Typography;
const { Option } = Select;
// const { TabPane } = Tabs;

const ProfilePage: React.FC = () => {
  // const { user } = useAuth();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  // const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [clubInfo, setClubInfo] = useState<any>(null);

  useEffect(() => {
    try {
      fetchUserData();
    } catch (error) {
      message.error('Произошла ошибка при загрузке профиля');
    }
  }, []);

  // Update form when userData changes
  useEffect(() => {
    if (userData && form) {
      // Use setTimeout to ensure form is fully mounted
      const timer = setTimeout(() => {
        const formData: any = {};
        
        if (userData.firstName) formData.firstName = userData.firstName;
        if (userData.lastName) formData.lastName = userData.lastName;
        if (userData.middleName) formData.middleName = userData.middleName;
        if (userData.email) formData.email = userData.email;
        if ((userData as any).phoneNumber) formData.phoneNumber = (userData as any).phoneNumber;
        if ((userData as any).telegram) formData.telegram = (userData as any).telegram;
        if (userData.dateOfBirth) {
          // Парсим дату из БД - dayjs автоматически определяет ISO формат
          const parsedDate = dayjs(userData.dateOfBirth);
          formData.dateOfBirth = parsedDate.isValid() ? parsedDate : undefined;
        }
        if (userData.gender) formData.gender = userData.gender;
        if (userData.weight) formData.weight = userData.weight;
        if (userData.beltLevel) formData.beltLevel = userData.beltLevel;
        if (userData.qualification) formData.qualification = userData.qualification;
        if (userData.experience) formData.experience = userData.experience;
        if (userData.clubId) formData.clubId = userData.clubId;
        
        form.setFieldsValue(formData);
      }, 0);
      
      return () => clearTimeout(timer);
    }
  }, [userData, form]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      // Токены теперь в httpOnly cookies, они отправляются автоматически
      // Проверка авторизации выполняется через AuthContext

      const data = await UserService.getCurrentUser();
     
      setUserData(data);

      if ((data as any).avatarUrl) {
        setAvatarUrl((data as any).avatarUrl);
      }

      // Загружаем информацию о клубе, если есть clubId
      if (data.clubId) {
        try {
          const clubResponse = await UserService.getClubById(data.clubId);
          if (clubResponse.data) {
            setClubInfo(clubResponse.data);
          }
        } catch (error) {
          logger.warn('Failed to load club info:', error);
          // Не показываем ошибку пользователю, так как это не критично
        }
      }

      // Form values will be set by useEffect when userData changes
    } catch (error: any) {
      logger.error('Error fetching user data:', error);

      // Show more specific error message
      if (error?.response?.status === 401) {
        message.error('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        message.error(`Не удалось загрузить данные пользователя: ${error?.message || 'Неизвестная ошибка'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    if (form && (userData?.role === 'participant' || userData?.role === 'coach')) {
      form.resetFields();
    }
    setEditing(false);

    // Reset form values to original user data
    if (userData) {
      form.setFieldsValue({
        firstName: userData.firstName,
        lastName: userData.lastName,
        middleName: userData.middleName,
        email: userData.email,
        phoneNumber: userData.phoneNumber,
        telegram: (userData as any).telegram,
        dateOfBirth: userData.dateOfBirth ? dayjs(userData.dateOfBirth) : undefined,
        gender: userData.gender,
        weight: userData.weight,
        beltLevel: userData.beltLevel,
        qualification: userData.qualification,
        experience: userData.experience
      });
    }
  };

  const handleChangePassword = async () => {
    try {
      setChangingPassword(true);
      const values = await passwordForm.validateFields();

      if (values.newPassword !== values.confirmPassword) {
        message.error('Новые пароли не совпадают');
        return;
      }

      await apiService.post('/users/change-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });

      message.success('Пароль успешно изменен');
      passwordForm.resetFields();
    } catch (error: any) {
      logger.error('Error changing password:', error);
      message.error(error.response?.data?.message || 'Не удалось изменить пароль');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!form) return;
      setLoading(true);
      const values = await form.validateFields();

      // Токены теперь в httpOnly cookies, они отправляются автоматически

      // Convert date to ISO string if it exists and prepare form data
      const formData: any = {};

      // Only include fields that are allowed to be updated
      const allowedFields = [
        'firstName', 'lastName', 'middleName', 'phoneNumber', 'telegram',
        'dateOfBirth', 'gender', 'weight', 'beltLevel',
        'qualification', 'experience'
        // 'email' is excluded as it's a read-only field in the form
      ];

      // Filter out undefined values and only include allowed fields
      allowedFields.forEach(field => {
        if (values[field] !== undefined) {
          formData[field] = values[field];
        }
      });

      // Special handling for date fields
      if (values.dateOfBirth) {
        formData.dateOfBirth = values.dateOfBirth.toISOString();
      }

      const updatedUser = await UserService.updateProfile(formData);
      setUserData(updatedUser);
      setEditing(false);
      message.success('Профиль успешно обновлен');
    } catch (error: any) {
      logger.error('Error updating profile:', error);

      // Provide more specific error messages based on the error response
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        if (status === 400) {
          // Handle validation errors
          message.error(`Ошибка валидации: ${errorData?.error || 'Проверьте введенные данные'}`);
        } else if (status === 401) {
          // Handle authentication errors
          message.error('Сессия истекла. Пожалуйста, войдите снова.');
        } else if (status === 403) {
          // Handle permission errors
          message.error('У вас нет прав для выполнения этого действия');
        } else if (status === 500) {
          // Handle server errors
          message.error('Ошибка сервера. Пожалуйста, попробуйте позже.');
          console.error('Server error details:', errorData);
        } else {
          // Handle other errors
          message.error(`Ошибка при обновлении профиля: ${errorData?.error || 'Неизвестная ошибка'}`);
        }
      } else if (error.request) {
        // Handle network errors
        message.error('Ошибка сети. Проверьте подключение к интернету.');
      } else {
        // Handle other errors
        message.error(`Ошибка при обновлении профиля: ${error.message || 'Неизвестная ошибка'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (options: any) => {
    const { file, onSuccess, onError } = options;

    try {
      setUploading(true);

      // Токены теперь в httpOnly cookies, они отправляются автоматически

      const url = await UserService.uploadAvatar(file);
      setAvatarUrl(url);
      onSuccess('ok');
      message.success('Аватар успешно загружен');
    } catch (error) {
      logger.error('Error uploading avatar:', error);
      onError(error);
      message.error('Ошибка при загрузке аватара');
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Вы можете загрузить только изображение!');
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Изображение должно быть меньше 2MB!');
      return false;
    }

    return true;
  };

  const uploadProps: UploadProps = {
    name: 'avatar',
    customRequest: handleAvatarUpload,
    beforeUpload,
    showUploadList: false,
    accept: 'image/*',
  };

  // Функции для локализации ролей и статусов
  const getRoleLabel = (role: string | undefined): string => {
    if (!role) return '-';
    const roleMap: { [key: string]: string } = {
      'admin': 'Администратор',
      'manager': 'Менеджер',
      'head_manager': 'Главный менеджер',
      'coach': 'Тренер',
      'participant': 'Участник',
      'user': 'Пользователь',
    };
    return roleMap[role] || role;
  };

  const getStatusLabel = (status: string | undefined): string => {
    if (!status) return '-';
    const statusMap: { [key: string]: string } = {
      'active': 'Активный',
      'inactive': 'Неактивный',
      'pending': 'Ожидает',
      'suspended': 'Приостановлен'
    };
    return statusMap[status] || status;
  };

  if (loading && !userData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      <Title level={2}>Профиль пользователя</Title>
      <Divider />

      <Row gutter={24}>
        <Col xs={24} md={8}>
          <ProfileCard>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <StyledAvatar
                src={avatarUrl}
                icon={!avatarUrl && <UserOutlined />}
                size={150}
              />

              <Upload {...uploadProps}>
                <Button
                  icon={<UploadOutlined />}
                  loading={uploading}
                >
                  Загрузить аватар
                </Button>
              </Upload>

              <Divider />

              {userData ? (
                <Descriptions
                  title="Информация о пользователе"
                  column={1}
                  bordered
                  style={{ width: '100%' }}
                >
                  <Descriptions.Item label="ID">{userData?.id || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Роль">{getRoleLabel(userData?.role)}</Descriptions.Item>
                  <Descriptions.Item label="Статус">{getStatusLabel(userData?.status)}</Descriptions.Item>
                  {userData?.clubId && (
                    <>
                      <Descriptions.Item label="ID клуба">{userData.clubId}</Descriptions.Item>
                      {clubInfo && (
                        <Descriptions.Item label="Клуб">{clubInfo.name || '-'}</Descriptions.Item>
                      )}
                    </>
                  )}
                  <Descriptions.Item label="Дата регистрации">
                    {userData?.createdAt ? dayjs(userData.createdAt).format('DD.MM.YYYY') : '-'}
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin />
                  <p>Загрузка данных пользователя...</p>
                </div>
              )}

              <Divider />

              <Link to="/subscriptions">
                <Button
                  type="primary"
                  icon={<CreditCardOutlined />}
                  block
                  style={{ marginTop: 16 }}
                >
                  Мои подписки
                </Button>
              </Link>
            </div>
          </ProfileCard>
        </Col>

        <Col xs={24} md={16}>
          {userData && userData.role === 'participant' ? (
            <Tabs
              defaultActiveKey="profile"
              items={[
                {
                  key: 'profile',
                  label: 'Профиль',
                  children: (
                    <ProfileCard
                      title="Личные данные"
                      extra={
                        editing ? (
                          <div>
                            <Button
                              type="primary"
                              icon={<SaveOutlined />}
                              onClick={handleSubmit}
                              style={{ marginRight: 8 }}
                              loading={loading}
                            >
                              Сохранить
                            </Button>
                            <Button
                              icon={<CloseOutlined />}
                              onClick={handleCancel}
                            >
                              Отмена
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={handleEdit}
                          >
                            Редактировать
                          </Button>
                        )
                      }
                    >
                      <Form
                        form={form}
                        layout="vertical"
                        disabled={!editing || loading}
                      >
                        <Row gutter={16}>
                          <Col xs={24} md={8}>
                            <Form.Item
                              name="lastName"
                              label="Фамилия"
                              rules={[{ required: true, message: 'Введите фамилию' }]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={8}>
                            <Form.Item
                              name="firstName"
                              label="Имя"
                              rules={[{ required: true, message: 'Введите имя' }]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={8}>
                            <Form.Item
                              name="middleName"
                              label="Отчество"
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={16}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="email"
                              label="Email"
                              rules={[
                                { required: true, message: 'Введите email' },
                                { type: 'email', message: 'Введите корректный email' }
                              ]}
                            >
                              <Input prefix={<MailOutlined />} disabled />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="phoneNumber"
                              label="Телефон"
                            >
                              <Input prefix={<PhoneOutlined />} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="telegram"
                              label="Telegram"
                            >
                              <Input placeholder="@username или ссылка" />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Divider orientation="left">Дополнительная информация</Divider>

                        <Row gutter={16}>
                          <Col xs={24} md={8}>
                            <Form.Item
                              name="dateOfBirth"
                              label="Дата рождения"
                              getValueFromEvent={getDateValueFromEvent}
                            >
                              <DatePicker 
                                {...datePickerProps}
                                placeholder="Выберите дату рождения (ДД.ММ.ГГГГ)"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={8}>
                            <Form.Item
                              name="gender"
                              label="Пол"
                            >
                              <Select placeholder="Выберите пол">
                                <Option value="male">Мужской</Option>
                                <Option value="female">Женский</Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={8}>
                            <Form.Item
                              name="weight"
                              label="Вес (кг)"
                            >
                              <Input type="number" min={0} step={0.1} />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={16}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="beltLevel"
                              label="Уровень пояса"
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="categoryId"
                              label="Категория"
                            >
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form>
                    </ProfileCard>
                  ),
                },
                {
                  key: 'statistics',
                  label: 'Статистика',
                  children: <ParticipantStatistics participantId={userData.id} />,
                },
                {
                  key: 'password',
                  label: 'Смена пароля',
                  children: (
                    <ProfileCard title="Смена пароля">
                      <Form
                        form={passwordForm}
                        layout="vertical"
                        onFinish={handleChangePassword}
                      >
                        <Form.Item
                          name="currentPassword"
                          label="Текущий пароль"
                          rules={[{ required: true, message: 'Введите текущий пароль' }]}
                        >
                          <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Введите текущий пароль"
                          />
                        </Form.Item>

                        <Form.Item
                          name="newPassword"
                          label="Новый пароль"
                          rules={[
                            { required: true, message: 'Введите новый пароль' },
                            { min: 8, message: 'Пароль должен содержать минимум 8 символов' }
                          ]}
                        >
                          <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Введите новый пароль"
                          />
                        </Form.Item>

                        <Form.Item
                          name="confirmPassword"
                          label="Подтвердите новый пароль"
                          dependencies={['newPassword']}
                          rules={[
                            { required: true, message: 'Подтвердите новый пароль' },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error('Пароли не совпадают'));
                              },
                            }),
                          ]}
                        >
                          <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Подтвердите новый пароль"
                          />
                        </Form.Item>

                        <Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={changingPassword}
                          >
                            Изменить пароль
                          </Button>
                        </Form.Item>
                      </Form>
                    </ProfileCard>
                  ),
                },
              ]}
            />
          ) : (
            <>
            <ProfileCard
              title="Личные данные"
              extra={
                editing ? (
                  <div>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleSubmit}
                      style={{ marginRight: 8 }}
                      loading={loading}
                    >
                      Сохранить
                    </Button>
                    <Button
                      icon={<CloseOutlined />}
                      onClick={handleCancel}
                    >
                      Отмена
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={handleEdit}
                  >
                    Редактировать
                  </Button>
                )
              }
            >
              <Form
                form={form}
                layout="vertical"
                disabled={!editing || loading}
              >
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="lastName"
                      label="Фамилия"
                      rules={[{ required: true, message: 'Введите фамилию' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="firstName"
                      label="Имя"
                      rules={[{ required: true, message: 'Введите имя' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="middleName"
                      label="Отчество"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: 'Введите email' },
                        { type: 'email', message: 'Введите корректный email' }
                      ]}
                    >
                      <Input prefix={<MailOutlined />} disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phoneNumber"
                      label="Телефон"
                    >
                      <Input prefix={<PhoneOutlined />} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="telegram"
                      label="Telegram"
                    >
                      <Input placeholder="@username или ссылка" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left">Дополнительная информация</Divider>

                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="dateOfBirth"
                      label="Дата рождения"
                      getValueFromEvent={getDateValueFromEvent}
                    >
                      <DatePicker 
                        {...datePickerProps}
                        placeholder="Выберите дату рождения (ДД.ММ.ГГГГ)"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="gender"
                      label="Пол"
                    >
                      <Select placeholder="Выберите пол">
                        <Option value="male">Мужской</Option>
                        <Option value="female">Женский</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="weight"
                      label="Вес (кг)"
                    >
                      <Input type="number" min={0} step={0.1} />
                    </Form.Item>
                  </Col>
                </Row>

                {userData && userData.role === 'coach' && (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="qualification"
                        label="Квалификация"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="experience"
                        label="Опыт (лет)"
                      >
                        <Input type="number" min={0} />
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                {userData && userData.clubId && (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="clubId"
                        label="ID клуба"
                      >
                        <Input disabled value={userData.clubId} />
                      </Form.Item>
                    </Col>
                    {clubInfo && (
                      <Col xs={24} md={12}>
                        <Form.Item
                          label="Название клуба"
                        >
                          <Input disabled value={clubInfo.name || '-'} />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                )}
              </Form>
            </ProfileCard>

            <ProfileCard title="Смена пароля" style={{ marginTop: 24 }}>
              <Form
                form={passwordForm}
                layout="vertical"
                onFinish={handleChangePassword}
              >
                <Form.Item
                  name="currentPassword"
                  label="Текущий пароль"
                  rules={[{ required: true, message: 'Введите текущий пароль' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Введите текущий пароль"
                  />
                </Form.Item>

                <Form.Item
                  name="newPassword"
                  label="Новый пароль"
                  rules={[
                    { required: true, message: 'Введите новый пароль' },
                    { min: 8, message: 'Пароль должен содержать минимум 8 символов' }
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Введите новый пароль"
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label="Подтвердите новый пароль"
                  dependencies={['newPassword']}
                  rules={[
                    { required: true, message: 'Подтвердите новый пароль' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Пароли не совпадают'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Подтвердите новый пароль"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={changingPassword}
                  >
                    Изменить пароль
                  </Button>
                </Form.Item>
              </Form>
            </ProfileCard>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ProfilePage;
